import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset! Please log in with your new password.");
      navigate("/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not reset password."); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" data-testid="reset-page">
        <Logo variant="lg" />
        {!token ? (
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">This reset link is invalid. Please request a new one.</p>
            <Link to="/forgot-password" className="mt-4 inline-block w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white">Request reset link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6">
            <div className="flex items-center gap-2"><Lock className="h-5 w-5 text-blue-500" /><h1 className="font-brand text-2xl font-bold text-slate-800">Set a new password</h1></div>
            <label className="mt-5 block text-sm font-medium text-slate-600">New password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password"
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-400" placeholder="At least 6 characters" />
            <label className="mt-4 block text-sm font-medium text-slate-600">Confirm password</label>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm"
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-400" placeholder="Re-enter password" />
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="mt-6 w-full rounded-xl brand-gradient-bg py-3 font-semibold text-white disabled:opacity-50">
              {loading ? "Saving..." : "Reset password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
